import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField,
} from "@mui/material";
import FieldCheckboxPicker from "common/components/FieldCheckboxPicker";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import {
  Field,
  useCurrentFarmAnimalGroups,
  useFields,
  useUpdateField,
} from "model";
import { useMemo, useState } from "react";
import { useUIStore } from "stores";

export default function AssignFieldDialog({
  animalGroupId,
  open,
  close,
  onAssign,
  animalGroupName,
  onApplyArea,
}: AssignFieldDialogProps) {
  const [areaScope, setAreaScope] = useState<"current" | "all">("current");
  const [field, setField] = useState<Field | null>(null);
  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fields = useMemo(
    () => allFields.filter((f) => f.animalGroup !== animalGroupId),
    [allFields, animalGroupId]
  );
  const [selection, setSelection] = useState<string[] | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();
  const groupName = animalGroupName ?? "this animal group";
  const assignmentName = (groupId?: string) =>
    !groupId
      ? "Silage"
      : groupId === animalGroupId
      ? groupName
      : animalGroups.find((group) => group.guid === groupId)?.name ??
        "Another animal group";
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const selectedIds =
    selection ??
    allFields.filter((f) => f.animalGroup === animalGroupId).map((f) => f.guid);
  const { t } = useTranslation();

  // Use React Query mutation for field updates
  const updateFieldMutation = useUpdateField();

  // Get onboarding completion dialog trigger
  const openOnboardingCompletionDialog = useUIStore(
    (state) => state.openOnboardingCompletionDialog
  );

  // Check if any field currently has an animal group assigned (before this assignment)
  const hasAnyAssignedField = useMemo(
    () =>
      allFields.some(
        (f) => f.animalGroup !== null && f.animalGroup !== undefined
      ),
    [allFields]
  );

  const assignedAreaHa = allFields
    .filter((f) => selectedIds.includes(f.guid))
    .reduce((sum, f) => sum + f.area / 10000, 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={Boolean(onApplyArea)}
      maxWidth="sm"
    >
      <DialogTitle
        sx={
          onApplyArea
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }
            : undefined
        }
      >
        {onApplyArea ? `Fields for ${groupName}` : t("assignFieldDialog.title")}
        {onApplyArea && (
          <Tooltip title="About field assignments">
            <IconButton
              size="small"
              aria-label="About field assignments"
              aria-expanded={showInfo}
              aria-controls="field-assignment-info"
              onClick={() => setShowInfo((value) => !value)}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent>
        {onApplyArea && (
          <Collapse in={showInfo}>
            <Typography
              id="field-assignment-info"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Checked fields are assigned to {groupName}. Checking a field moves
              it from its previous animal group; unchecking moves it to Silage.
              Field assignments and the budget area change when you save.
            </Typography>
          </Collapse>
        )}
        {onApplyArea ? (
          <FieldCheckboxPicker
            fields={allFields}
            selectedIds={selectedIds}
            renderLabel={(guid, name) => {
              const f = allFields.find((item) => item.guid === guid);
              return (
                <Box component="span" sx={{ display: "block", py: 0.5 }}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ display: "block" }}
                  >
                    {name}{" "}
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      · {((f?.area ?? 0) / 10000).toFixed(1)} ha
                    </Box>
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {assignmentName(f?.animalGroup)}
                  </Typography>
                </Box>
              );
            }}
            disabled={saving}
            onToggle={(guid) =>
              setSelection(
                selectedIds.includes(guid)
                  ? selectedIds.filter((id) => id !== guid)
                  : [...selectedIds, guid]
              )
            }
          />
        ) : (
          <Autocomplete
            disabled={fields.length === 0}
            options={fields}
            getOptionLabel={(field) => field.name}
            style={{ width: "100%" }}
            onChange={(_, newValue) => setField(newValue)}
            value={field}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  fields.length === 0
                    ? "assignFieldDialog.nofieldsLabel"
                    : "assignFieldDialog.inputLabel"
                )}
                variant="outlined"
              />
            )}
          />
        )}
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
        {onApplyArea && (
          <>
            {allFields.some(
              (f) =>
                selectedIds.includes(f.guid) !==
                (f.animalGroup === animalGroupId)
            ) && (
              <Box
                sx={{
                  mt: 2,
                  px: 1.5,
                  py: 1,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Changes
                </Typography>
                {allFields
                  .filter(
                    (f) =>
                      selectedIds.includes(f.guid) !==
                      (f.animalGroup === animalGroupId)
                  )
                  .map((f) => (
                    <Typography
                      key={f.guid}
                      variant="body2"
                      color="text.secondary"
                    >
                      {f.name}: {assignmentName(f.animalGroup)} →{" "}
                      {selectedIds.includes(f.guid) ? groupName : "Silage"}
                    </Typography>
                  ))}
              </Box>
            )}
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Selected area: {assignedAreaHa.toFixed(1)} ha ·{" "}
                {selectedIds.length}{" "}
                {selectedIds.length === 1 ? "field" : "fields"}
              </Typography>
              <FormControl
                disabled={saving}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { sm: "center" },
                  gap: { sm: 1.5 },
                }}
              >
                <FormLabel
                  id="field-area-scope-label"
                  sx={{ typography: "body2" }}
                >
                  Apply to
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="field-area-scope-label"
                  value={areaScope}
                  onChange={(_, value) =>
                    setAreaScope(value === "all" ? "all" : "current")
                  }
                >
                  <FormControlLabel
                    value="current"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">Current month</Typography>
                    }
                  />
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">All months</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={onApplyArea ? { px: 3, pb: 2, pt: 1 } : undefined}>
        <Button onClick={onClose} color="secondary">
          {onApplyArea ? "Cancel" : t("settings.button.close")}
        </Button>
        {onApplyArea ? (
          <Button
            disabled={saving}
            onClick={() => void saveSelection()}
            color="primary"
            variant="contained"
          >
            Save changes
          </Button>
        ) : (
          <Button
            disabled={!field || updateFieldMutation.isPending}
            onClick={assign}
            color="primary"
          >
            {t("assignFieldDialog.button")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  function onClose() {
    if (saving) return;
    setSelection(null);
    setShowInfo(false);
    setSaveError(null);
    setField(null);
    setAreaScope("current");
    close();
  }

  async function saveSelection() {
    setSaving(true);
    setSaveError(null);
    try {
      const changedFields = allFields.filter(
        (f) =>
          selectedIds.includes(f.guid) !== (f.animalGroup === animalGroupId)
      );
      for (const f of changedFields) {
        const adding = selectedIds.includes(f.guid);
        await updateFieldMutation.mutateAsync({
          ...f,
          animalGroup: adding ? animalGroupId : undefined,
        });
        if (adding) {
          analytics.track(AnalyticsEvents.ANIMAL_GROUP_FIELD_ASSIGNED);
          onAssign?.(f);
        }
      }
      onApplyArea?.(assignedAreaHa, areaScope);
      if (!hasAnyAssignedField && selectedIds.length > 0)
        openOnboardingCompletionDialog();
      setSelection(null);
      setShowInfo(false);
      setAreaScope("current");
      close();
    } catch {
      setSaveError("Could not save all field assignments. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function assign() {
    if (!field) return;
    analytics.track(AnalyticsEvents.ANIMAL_GROUP_FIELD_ASSIGNED);
    const isFirstAssignment = !hasAnyAssignedField;
    const assignedField = field;
    updateFieldMutation.mutate(
      { ...field, animalGroup: animalGroupId },
      {
        onSuccess: () => {
          setField(null);
          close();
          if (isFirstAssignment) {
            openOnboardingCompletionDialog();
          }
          onAssign?.(assignedField);
          onApplyArea?.(assignedAreaHa + assignedField.area / 10000, areaScope);
          setAreaScope("current");
        },
      }
    );
  }
}

type AssignFieldDialogProps = {
  open: boolean;
  close: () => void;
  animalGroupId: string;
  onAssign?: (field: Field) => void;
  animalGroupName?: string;
  onApplyArea?: (areaHa: number, scope: "current" | "all") => void;
};
